import { dependencies } from '@pn/core/dependencies';
import type { DataItemId } from '@pn/core/domain/data';
import type {
  ProductionChartType,
  ProductionItem,
} from '@pn/core/domain/production';
import { useInjectableReducer } from '@pn/core/storage/injectableReducers';
import { useBuildSliceSelector } from '@pn/services/redux/useBuildSliceSelector';
import { isNil } from 'lodash-es';
import { createProductionSlice } from './productionSlice';

export const useProductionStorage = (
  productionChartType: ProductionChartType
) => {
  const { name, getInitialState, reducer } =
    createProductionSlice(productionChartType);

  const useSliceSelector = useBuildSliceSelector(name, getInitialState());

  useInjectableReducer(name, reducer);

  return {
    isFetching: useSliceSelector((state) => state.isFetching),
    isFetchingExtra: useSliceSelector((state) => state.isFetchingExtra),
    isError: useSliceSelector((state) => state.isError),
    productionItems: useSliceSelector((state) => state.productionItems),
    idsLoaded: useSliceSelector((state) => state.idsLoaded),
    loadedKey: useSliceSelector((state) => state.loadedKey),
  };
};

export const productionActions = (productionChartType: ProductionChartType) => {
  const { dispatch, getState } = dependencies.store;
  const { actions, name } = createProductionSlice(productionChartType);

  if (isNil(getState()[name])) {
    throw new Error(`[${name}] reducer has not been injected yet`);
  }

  return {
    request: (key?: string) => dispatch(actions.request(key)),
    requestExtra: (ids: DataItemId[]) => dispatch(actions.requestExtra(ids)),
    error: () => dispatch(actions.error()),
    receive: (productionItems: ProductionItem[]) =>
      dispatch(actions.receive(productionItems)),
    receiveExtra: (productionItems: ProductionItem[]) =>
      dispatch(actions.receiveExtra(productionItems)),
    removeExtra: (ids: DataItemId[]) => dispatch(actions.removeExtra(ids)),
  };
};
